import React from "react";
import { formatMessage } from "lib/globalize-util";
import { Set } from "immutable";
import { AlertDialog } from "components/dialog/alert";
import { AlertNotification } from "components/alert/alert-notification";
import { SeverityMessages } from "constants/product-collection";
import Button from "components/form/button";
import { getDeliveryPurchaseOrderFieldLabel } from "helpers/delivery-purchase-order";
import { DATATEST } from "constants/integration";
import { exportMerchantStyles, styles } from "./export-styles";

interface IProps {
    onRequestClose: () => void;
    open: boolean;
    title: string;
    onExport: () => void;
    requiredFieldNames: string[];
    desiredFieldNames: string[];
    bypassDesiredConditions: boolean;
    itemDisplayProperty: any;
    requiredFieldsEmpty: boolean;
    desiredFieldsEmpty: boolean;
    isGridGrouped: boolean;
    deliveryPORequiredFields: string[];
    deliveryPODesiredFields: string[];
}

export const ExportMerchantDialog: React.FC<IProps> = ({
    title,
    bypassDesiredConditions,
    open,
    onRequestClose,
    onExport,
    requiredFieldNames,
    desiredFieldNames,
    itemDisplayProperty,
    requiredFieldsEmpty,
    desiredFieldsEmpty,
    deliveryPORequiredFields,
    deliveryPODesiredFields,
    isGridGrouped,
}) => {
    const getFieldNames = React.useCallback(
        (fieldKeyNames, deliveryPOFields) => {
            const fieldsNames = itemDisplayProperty.toJS();
            const requiredFields = [];
            const fieldKeyNamesSet = Set<string>(fieldKeyNames);

            fieldsNames.forEach((fieldName) => {
                if (fieldKeyNamesSet.has(fieldName.field_name)) {
                    requiredFields.push(fieldName.display_name);
                }
            });

            deliveryPOFields.forEach((deliveryPOField) => {
                const fieldLabel = getDeliveryPurchaseOrderFieldLabel(deliveryPOField);
                fieldLabel && requiredFields.push(fieldLabel);
            });

            return requiredFields;
        },
        [itemDisplayProperty]
    );

    const shouldDisableExportButton = React.useMemo(() => {
        if (isGridGrouped) {
            return false;
        }

        if (bypassDesiredConditions) {
            return !requiredFieldsEmpty;
        } else {
            return !requiredFieldsEmpty || !desiredFieldsEmpty;
        }
    }, [requiredFieldsEmpty, desiredFieldsEmpty, bypassDesiredConditions, isGridGrouped]);

    const showRequiredMessage = React.useMemo(() => {
        return (requiredFieldNames?.length > 0 && !requiredFieldsEmpty) || deliveryPORequiredFields.length > 0;
    }, [requiredFieldNames, requiredFieldsEmpty, deliveryPORequiredFields]);

    const showDesiredMessage = React.useMemo(() => {
        return (desiredFieldNames?.length > 0 && !desiredFieldsEmpty) || deliveryPODesiredFields.length > 0;
    }, [desiredFieldNames, desiredFieldsEmpty, deliveryPODesiredFields]);

    const getErrorMessage = React.useCallback(() => {
        const fieldNames = getFieldNames(requiredFieldNames, deliveryPORequiredFields);
        if (fieldNames.length > 1) {
            const lastFieldName = fieldNames.pop();
            return formatMessage(
                "Missing required fields. |requiredFields| and |lastRequiredField|. Please fill in the missing fields and try your export again.",
                {
                    requiredFields: fieldNames.join(", "),
                    lastRequiredField: lastFieldName,
                }
            );
        }

        return formatMessage(
            "Missing required fields. |requiredFields|. Please fill in the missing fields and try your export again.",
            {
                requiredFields: fieldNames.join(", "),
            }
        );
    }, [requiredFieldNames, deliveryPORequiredFields]); // eslint-disable-line react-hooks/exhaustive-deps

    const getWarningMessage = React.useCallback(() => {
        const fieldNames = getFieldNames(desiredFieldNames, deliveryPODesiredFields);

        if (fieldNames.length > 1) {
            const lastFieldName = fieldNames.pop();
            return formatMessage(
                "Missing recommended fields. |desiredFields| and |lastDesiredField|. Although this won't prevent your export, we strongly encourage that you fill out prior to exporting.",
                { desiredFields: fieldNames.join(", "), lastDesiredField: lastFieldName }
            );
        }

        return formatMessage(
            "Missing recommended fields. |desiredFields|. Although this won't prevent your export, we strongly encourage that you fill out prior to exporting.",
            {
                desiredFields: fieldNames.join(", "),
            }
        );
    }, [desiredFieldNames, deliveryPODesiredFields]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <AlertDialog
            dismissButton={false}
            onRequestClose={onRequestClose}
            open={open}
            title={title}
            contentStyle={{ maxWidth: "600px" }}
        >
            {isGridGrouped && <p>{formatMessage("Please ungroup your data before exporting.")}</p>}
            {!isGridGrouped && (
                <>
                    <p>
                        {formatMessage(
                            "In some instances, certain issues will prevent you from exporting your assortment. Address what's higlighted and try again."
                        )}
                    </p>
                    {showRequiredMessage && (
                        <AlertNotification
                            className="notification"
                            message={getErrorMessage()}
                            severity={SeverityMessages.Error}
                            open={true}
                            noMargin
                            customStyles={exportMerchantStyles}
                            dataTest={DATATEST.ASSORTMENTS.EXPORT_MERCHANT_REQUIRED_ALERT}
                        />
                    )}
                    {showDesiredMessage && (
                        <AlertNotification
                            className="notification"
                            customStyles={exportMerchantStyles}
                            message={getWarningMessage()}
                            severity={SeverityMessages.Warning}
                            open={true}
                            noMargin
                            dataTest={DATATEST.ASSORTMENTS.EXPORT_MERCHANT_DESIRED_ALERT}
                        />
                    )}
                    <footer style={styles.buttons}>
                        <Button
                            label={formatMessage("Close")}
                            onClick={onRequestClose}
                            style={styles.button.cancel}
                            data-test={DATATEST.ASSORTMENTS.EXPORT_MERCHANT_CLOSE_BTN_DIALOG}
                        />
                        <Button
                            label={formatMessage("Export")}
                            primary={true}
                            onClick={onExport}
                            style={styles.button.export}
                            disabled={shouldDisableExportButton}
                            data-test={DATATEST.ASSORTMENTS.EXPORT_MERCHANT_BTN_DIALOG}
                        />
                    </footer>
                </>
            )}
        </AlertDialog>
    );
};
